// modal.ts — Universal modal animation system.
//
// Exports reusable animation functions for any modal (omnisearch, avatar, etc.)
// and a generic `Modal` hook for simple modals that only need open/close.
//
// Standard markup convention:
//   <div id="..." phx-hook="Modal" data-close-event="my_close">
//     <div class="modal-backdrop fixed inset-0 bg-black/50"></div>
//     <div class="modal-panel awaiting-gsap relative ...">content</div>
//   </div>

import gsap from "gsap";
import { computeFocusTarget, computeShellState } from "./modal-state";
import type { LiveViewHook } from "./types";
// Close-event wire form is dox-owned (`ModalCloseEvent` union in
// `ui/chrome.dox`). The hook validates `data-close-event` at mount via
// `validModalCloseEvent` — a HEEx typo throws "Invalid ModalCloseEvent
// `xxx`" loud and early instead of pushing a string the LV silently
// ignores. No literal fallback: an unset `data-close-event` is itself
// the typo to catch.
//
// CSS class names shared with HEEx are dox-owned too (`CssClassModal`
// in `ui/css-classes.dox`) — `OperationWeb.UI.Modal` renders
// `.modal-backdrop`, `.modal-panel.awaiting-gsap`; the top-level
// layout renders `.app-shell`. Pinned both sides so a rename surfaces
// uniformly.
import {
  validModalCloseEvent,
  illuminateCssClassModal,
} from "../../../.dox/ui";
import type { ModalCloseEvent } from "../../../.dox/ui";

const MODAL_PANEL = illuminateCssClassModal("modalPanel");
const MODAL_BACKDROP = illuminateCssClassModal("modalBackdrop");
const AWAITING_GSAP = illuminateCssClassModal("awaitingGsap");
const APP_SHELL = illuminateCssClassModal("appShell");

// -- Animation constants -----------------------------------------------------

const DURATION = 0.3;
const MODAL_OPEN_CLASS = "modal-open";

type ModalRuntimeState = {
  count: number;
  shellOverflow: string;
  shellClosing: boolean;
};

const runtimeState = (): ModalRuntimeState => {
  const key = "__operationModalState";
  const globalState = window as typeof window & { [key]?: ModalRuntimeState };

  if (!globalState[key]) {
    globalState[key] = {
      count: 0,
      shellOverflow: "",
      shellClosing: false,
    };
  }

  return globalState[key];
};

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(", ");

const portalModalRoot = (root: HTMLElement): void => {
  // Layout-owned/global modals already render outside `.app-shell`.
  // Moving those a second time detaches them from their child LV root,
  // so the next phx-change patch can look like a destroy/close.
  if (!root.closest(`.${APP_SHELL}`)) return;

  const appShellParent = document.querySelector(`.${APP_SHELL}`)?.parentElement;
  if (appShellParent && root.parentElement !== appShellParent) {
    appShellParent.appendChild(root);
  }
};

const getFocusable = (root: HTMLElement): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(focusableSelector)).filter(
    (el) => !el.hasAttribute("inert") && !el.getAttribute("aria-hidden"),
  );

// Single-source-of-truth: shell interactivity is derived from state.count
// AND the live DOM state. Reading the actual `inert` attribute (instead of
// trusting a parallel `shellDisabled` flag) means a LiveView patch that
// wipes `inert` from the shell can be re-asserted on the next reconcile —
// the failure mode the wallaby `stacked modal cleanup` test caught.
// Idempotent — safe to call when nothing has changed.
//
// The pure decision (count + closing → wantDisabled + wantOpenClass)
// lives in modal-state.ts; this wrapper only mutates the DOM.
const reconcileShellInteractivity = (): void => {
  const shell = document.querySelector<HTMLElement>(`.${APP_SHELL}`);
  if (!shell) return;

  const state = runtimeState();
  const { wantDisabled, wantOpenClass } = computeShellState({
    openCount: state.count,
    closing: state.shellClosing,
  });
  const isDisabled = shell.hasAttribute("inert");

  if (wantDisabled && !isDisabled) {
    state.shellOverflow = shell.style.overflow;
    shell.style.overflow = "hidden";
    shell.setAttribute("inert", "");
    document.documentElement.classList.toggle(MODAL_OPEN_CLASS, wantOpenClass);
  } else if (!wantDisabled && isDisabled) {
    shell.style.overflow = state.shellOverflow;
    shell.removeAttribute("inert");
    document.documentElement.classList.remove(MODAL_OPEN_CLASS);
    state.shellClosing = false;
  } else if (wantDisabled) {
    document.documentElement.classList.toggle(MODAL_OPEN_CLASS, wantOpenClass);
  } else {
    if (document.documentElement.classList.contains(MODAL_OPEN_CLASS)) {
      document.documentElement.classList.remove(MODAL_OPEN_CLASS);
    }
    state.shellClosing = false;
  }
};

export const syncModalShellState = (): void => {
  const shell = document.querySelector<HTMLElement>(`.${APP_SHELL}`);
  if (!shell) return;
  const state = runtimeState();
  if (state.count > 0 && !shell.hasAttribute("inert")) {
    shell.setAttribute("inert", "");
  }
  document.documentElement.classList.toggle(
    MODAL_OPEN_CLASS,
    state.count > 0 && !state.shellClosing,
  );
};

// Both session functions return a "was this the first/last modal?"
// boolean. Today no caller reads it (modalOpen and modalCleanup
// discard the return). Keep the value here in case a future caller
// wants the boundary signal, but treat the return as informational
// — Stryker's boundary mutants on it are equivalent until someone
// actually consumes the value.
const beginModalSession = (): boolean => {
  const state = runtimeState();
  const firstModal = state.count === 0;
  state.shellClosing = false;
  state.count += 1;
  reconcileShellInteractivity();
  return firstModal;
};

const endModalSession = (): boolean => {
  const state = runtimeState();
  state.count = Math.max(0, state.count - 1);
  reconcileShellInteractivity();
  return state.count === 0;
};

export const hasActiveModal = (): boolean => runtimeState().count > 0;

export const activateModalA11y = (
  root: HTMLElement,
  panel: HTMLElement | null,
): HTMLElement | null => {
  const active =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  const target = panel ?? root;

  root.setAttribute("role", "dialog");
  root.setAttribute("aria-modal", "true");
  target.tabIndex = target.tabIndex >= 0 ? target.tabIndex : -1;
  target.focus();

  window.setTimeout(() => {
    const focusable = getFocusable(target);
    (focusable[0] ?? target).focus();
  }, 0);

  return active;
};

export const restoreModalFocus = (el: HTMLElement | null): void => {
  if (el && document.contains(el)) {
    el.focus();
    return;
  }
  // Captured element was detached (e.g. LiveView re-rendered the trigger).
  // Fall back to re-querying by aria-label if we still have it — this lets
  // keyboard users return to a semantically-equivalent trigger rather than
  // losing focus to <body>.
  if (el) {
    const label = el.getAttribute("aria-label");
    if (label) {
      const replacement = document.querySelector<HTMLElement>(
        `[aria-label="${CSS.escape(label)}"]`,
      );
      replacement?.focus();
    }
  }
};

export const trapModalFocus = (
  e: KeyboardEvent,
  root: HTMLElement,
  panel: HTMLElement | null,
): void => {
  if (e.key !== "Tab") return;

  const target = panel ?? root;
  const focusable = getFocusable(target);
  if (focusable.length === 0) {
    e.preventDefault();
    target.focus();
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!first || !last) {
    e.preventDefault();
    target.focus();
    return;
  }

  const active = document.activeElement as HTMLElement | null;
  // `focusedInside` means: an interior focusable owns focus. When the
  // panel/root itself (`active === target`) is focused — typical right
  // after activateModalA11y fires before any keystroke — we treat it
  // as "not focused inside the trap content" so the shift-tab branch
  // wraps to last (matches the old `active === target` leg) and the
  // forward-tab branch wraps to first.
  const focusedInside =
    active !== null && active !== target && target.contains(active);
  const next = computeFocusTarget({
    active,
    first,
    last,
    shiftKey: e.shiftKey,
    focusedInside,
  });
  if (next instanceof HTMLElement) {
    e.preventDefault();
    next.focus();
  }
};

let _focusGuardActive = false;

export const keepModalFocus = (
  root: HTMLElement,
  panel: HTMLElement | null,
  activeTarget: EventTarget | null,
): void => {
  if (_focusGuardActive) return;
  if (activeTarget instanceof Node && root.contains(activeTarget)) return;

  const target = panel ?? root;
  const focusable = getFocusable(target);
  _focusGuardActive = true;
  (focusable[0] ?? target).focus();
  _focusGuardActive = false;
};

// -- Exported animation primitives -------------------------------------------

export const modalOpen = (
  _shell: HTMLElement | null,
  panel: HTMLElement | null,
): void => {
  beginModalSession();

  if (panel) {
    panel.classList.remove(AWAITING_GSAP);
    gsap.fromTo(
      panel,
      {
        z: 200,
        opacity: 0,
        filter: "blur(2px)",
        transformPerspective: 1000,
        force3D: true,
        willChange: "transform, opacity, filter",
      },
      {
        z: 0,
        opacity: 1,
        filter: "blur(0px)",
        duration: DURATION,
        ease: "power2.out",
        transformPerspective: 1000,
        force3D: true,
        willChange: "transform, opacity, filter",
        // Hooks inside the panel (e.g. MarkdownEditor / CodeMirror) need
        // to recompute their geometry once the panel is at its final
        // opacity/transform — measuring while opacity:0 + transform gives
        // CodeMirror a 0-width box it never recovers from. The bubbling
        // `modal:opened` event lets descendants react without the panel
        // hook needing to know about them.
        onComplete: () => {
          panel.dispatchEvent(
            new CustomEvent("modal:opened", { bubbles: true }),
          );
        },
      },
    );
  }
};

export const modalClose = (el: HTMLElement, onComplete: () => void): void => {
  const panel = el.querySelector(`.${MODAL_PANEL}`);
  const backdrop = el.querySelector(`.${MODAL_BACKDROP}`);
  const state = runtimeState();
  if (state.count <= 1) {
    state.shellClosing = true;
    // Start the shell return transition immediately while the panel/backdrop
    // animate out; inert stays until modalCleanup drains the session count.
    reconcileShellInteractivity();
  }
  let done = false;
  const finish = (): void => {
    if (done) return;
    done = true;
    onComplete();
  };

  if (panel) {
    gsap.to(panel, {
      z: 60,
      y: 8,
      opacity: 0,
      filter: "blur(2px)",
      duration: DURATION,
      ease: "power2.in",
      transformPerspective: 1000,
      force3D: true,
      willChange: "transform, opacity, filter",
      onComplete: finish,
    });
  } else {
    finish();
  }

  if (backdrop) {
    gsap.to(backdrop, { opacity: 0, duration: DURATION });
  }
};

export const modalCleanup = (): void => {
  endModalSession();
};

// -- Generic Modal hook ------------------------------------------------------
// For simple modals that only need animated open/close.
// Complex modals (omnisearch) import modalOpen/modalClose directly.

// The Modal hook only ever pushes a ModalCloseEvent up to the LV
// (Receives is unused — it never calls handleEvent), so we narrow Sends
// to `ModalCloseEvent` and leave Receives at the default. A typo in
// pushEvent is now a tsc error sourced from the same dox illuminate
// block the Elixir handle_event clauses match.
interface ModalHook extends LiveViewHook<string, ModalCloseEvent> {
  _closing: boolean;
  _escHandler: (e: KeyboardEvent) => void;
  _focusTrapHandler: (e: KeyboardEvent) => void;
  _focusGuardHandler: (e: FocusEvent) => void;
  _restoreFocusEl: HTMLElement | null;
}

export const Modal = {
  mounted(this: ModalHook): void {
    // Keep the modal outside app-shell so shell transforms don't trap
    // fixed positioning, even if LiveView patches try to move it back.
    portalModalRoot(this.el);

    const panel = this.el.querySelector<HTMLElement>(`.${MODAL_PANEL}`);
    const shell = document.querySelector<HTMLElement>(`.${APP_SHELL}`);
    // No string-literal fallback: an unset attribute is the typo
    // we want to catch. `validModalCloseEvent(undefined)` throws
    // "Invalid ModalCloseEvent `undefined`" — exactly the loud failure
    // the spec asks for.
    const closeEvent = validModalCloseEvent(this.el.dataset["closeEvent"]);

    this._closing = false;
    this._restoreFocusEl = activateModalA11y(this.el, panel);
    modalOpen(shell, panel);

    const animatedClose = (): void => {
      if (this._closing) return;
      this._closing = true;
      modalClose(this.el, () => {
        this.pushEvent(closeEvent, {});
      });
    };

    // Backdrop click
    const backdrop = this.el.querySelector(`.${MODAL_BACKDROP}`);
    if (backdrop)
      backdrop.addEventListener("click", (e: Event) => {
        e.stopPropagation();
        animatedClose();
      });

    // Escape key
    this._escHandler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        animatedClose();
      }
    };
    document.addEventListener("keydown", this._escHandler);

    this._focusTrapHandler = (e: KeyboardEvent): void => {
      trapModalFocus(e, this.el, panel);
    };
    this.el.addEventListener("keydown", this._focusTrapHandler);

    this._focusGuardHandler = (e: FocusEvent): void => {
      keepModalFocus(this.el, panel, e.target);
    };
    document.addEventListener("focusin", this._focusGuardHandler);
  },

  updated(this: ModalHook): void {
    portalModalRoot(this.el);
    // Morphdom re-applies awaiting-gsap from the template on every patch.
    // Strip it immediately so the panel stays visible after initial animation.
    const panel = this.el.querySelector<HTMLElement>(`.${AWAITING_GSAP}`);
    if (panel) panel.classList.remove(AWAITING_GSAP);
    // Idempotent — only writes to the shell if LiveView wiped our class.
    syncModalShellState();
  },

  disconnected(this: ModalHook): void {
    // LiveView socket dropped — close modal immediately
    document.removeEventListener("keydown", this._escHandler);
    this.el.removeEventListener("keydown", this._focusTrapHandler);
    document.removeEventListener("focusin", this._focusGuardHandler);
    modalCleanup();
    this.el.remove();
  },

  destroyed(this: ModalHook): void {
    document.removeEventListener("keydown", this._escHandler);
    this.el.removeEventListener("keydown", this._focusTrapHandler);
    document.removeEventListener("focusin", this._focusGuardHandler);
    restoreModalFocus(this._restoreFocusEl);

    // portalModalRoot moves this.el outside LiveView's virtual-DOM root,
    // so morphdom only removes the placeholder when the server flips the
    // assign — the portaled element is still ours to dispose of.
    //
    // Two paths land here:
    //
    //   1. User-initiated close (backdrop / ESC): `animatedClose` already
    //      ran its GSAP timeline and set `_closing = true`. The modal
    //      shell + panel are at their end-state (opacity 0, blurred).
    //      Just clean up and remove — replaying modalClose now would
    //      animate from 0 to 0 and look like a stutter (the jank).
    //
    //   2. Server-initiated close (Save submit, programmatic editing=false):
    //      `_closing` is false, no animation has played yet. Run it here
    //      so the panel slides out instead of popping.
    if (this._closing) {
      modalCleanup();
      this.el.remove();
    } else {
      this._closing = true;
      modalClose(this.el, () => {
        modalCleanup();
        this.el.remove();
      });
    }
  },
};

// -- <app-modal> custom element: portal-on-connect --------------------------
//
// The Modal hook portals the element outside `.app-shell` on mount. That
// alone isn't enough: whenever a LiveView that owns an `<app-modal>`
// re-renders (runner heartbeats, PubSub, periodic refresh), morphdom keys
// by `id` and relocates the element back to its template position INSIDE
// the shell. The hook's `updated()` doesn't reliably fire on pure
// relocations — when the element's attrs/content didn't change, Phoenix
// considers it unchanged and skips the hook callback — so the modal stays
// trapped inside the shell's blur and looks broken.
//
// Registering `<app-modal>` as a custom element catches the disconnect/
// reconnect the relocation triggers via `connectedCallback`. The microtask
// defer is load-bearing: reparenting synchronously from inside
// `connectedCallback` happens mid-walk for morphdom, which desyncs its
// tree cursors. Letting morphdom finish its synchronous patch first, then
// re-portaling, keeps both subsystems consistent.
//
// (Custom element names must contain a hyphen per the HTML spec, which is
// why this is `app-modal` and not `modal`.)
class AppModalElement extends HTMLElement {
  connectedCallback(): void {
    queueMicrotask(() => {
      if (this.isConnected) portalModalRoot(this);
    });
  }
}

if (typeof customElements !== "undefined" && !customElements.get("app-modal")) {
  customElements.define("app-modal", AppModalElement);
}
